/**
 * MobileBottomNav — Floating pill bottom navigation for mobile/tablet.
 *
 * Matches the "modern Reddit" reference (2024–2026):
 *   • Floating pill inset from viewport edges (see MobileBottomNav.css)
 *   • Exactly 5 items (Reddit-standard density)
 *   • Duotone icons: filled variant when active, outlined when inactive
 *   • Label only for the active tab (icon-only inactive)
 *   • env(safe-area-inset-bottom) respected via --safe-bottom in index.css
 *   • Theme cycle moved into More menu (previously stole a slot)
 *
 * ponytail: only renders at ≤768px via CSS display:none on desktop.
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import {
  Search as SearchOutline,
  SearchFilled,
  Availability as AvailabilityOutline,
  AvailabilityFilled,
  Observation as ObservationsOutline,
  ObservationFilled as ObservationsFilled,
  Statistics as StatsOutline,
  StatisticsFilled as StatsFilled,
  More as MoreOutline,
  MoreFilled,
} from '../icons';
import './MobileBottomNav.css';

// Icon components — outlined variant + filled variant per glyph, from the shared icon
// module. Active state swaps outlined → filled (real fill, not a thicker stroke); the
// swap is layered on aria-current + the active label, never the sole cue.

const navItems = [
  { id: 'search',       label: 'Search',       path: '/search/station', Outline: SearchOutline,       Filled: SearchFilled },
  { id: 'availability', label: 'Availability', path: '/availability',   Outline: AvailabilityOutline, Filled: AvailabilityFilled },
  { id: 'observations', label: 'Observations', path: '/observations',   Outline: ObservationsOutline, Filled: ObservationsFilled },
  { id: 'statistics',   label: 'Stats',        path: '/network/statistics', Outline: StatsOutline,     Filled: StatsFilled },
] as const;

// Theme cycle helpers (moved from primary nav row into the More menu).
const cycleTheme = () => {
  const root = document.documentElement;
  const current = root.getAttribute('data-theme') || localStorage.getItem('stationExplorer_theme') || 'system';
  const next = current === 'dark' ? 'light' : current === 'light' ? 'system' : 'dark';
  if (next === 'system') {
    root.removeAttribute('data-theme');
    root.style.colorScheme = '';
  } else {
    root.setAttribute('data-theme', next);
    root.style.colorScheme = next;
  }
  localStorage.setItem('stationExplorer_theme', next);
};

const currentTheme = (): string => {
  if (typeof document === 'undefined') return 'system';
  return document.documentElement.getAttribute('data-theme') || localStorage.getItem('stationExplorer_theme') || 'system';
};

export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActiveSection } = useAppStore();
  const [moreOpen, setMoreOpen] = useState(false);

  const getActiveId = () => {
    const path = location.pathname;
    if (path.startsWith('/search')) return 'search';
    if (path.startsWith('/availability')) return 'availability';
    if (path.startsWith('/observations')) return 'observations';
    // Stats lives at /network/statistics (and the /statistics redirect shim). Match
    // it BEFORE the /network catch-all so the real URL lights "Stats", not "More".
    if (path === '/network/statistics' || path.startsWith('/statistics')) return 'statistics';
    if (path.startsWith('/station-quality') || path.startsWith('/network')) return 'more';
    return 'search';
  };

  const activeId = getActiveId();

  const handleTap = (item: typeof navItems[number]) => {
    setMoreOpen(false);
    setActiveSection(item.id as any);
    navigate(item.path);
  };

  // Left/Right arrow navigation between nav items (WAI-ARIA APG toolbar pattern).
  // Home/End jump to first/last. Escape closes the More popover.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' && moreOpen) {
      setMoreOpen(false);
      e.preventDefault();
      return;
    }
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
    const items = Array.from(
      (e.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('button.mobile-nav-item')
    );
    if (items.length === 0) return;
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    let next = currentIndex;
    if (e.key === 'ArrowRight') next = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
    if (e.key === 'ArrowLeft')  next = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End')  next = items.length - 1;
    items[next]?.focus();
    e.preventDefault();
  };

  return (
    <nav className="mobile-bottom-nav" aria-label="Main navigation" onKeyDown={handleKeyDown}>
      {moreOpen && (
        <div className="mobile-more-menu" role="menu">
          <button className="mobile-more-item" role="menuitem" onClick={() => { setMoreOpen(false); navigate('/network'); }}>
            Health
          </button>
          <button className="mobile-more-item" role="menuitem" onClick={() => { setMoreOpen(false); navigate('/quality'); }}>
            QC
          </button>
          <button className="mobile-more-item" role="menuitem" onClick={() => { setMoreOpen(false); cycleTheme(); }}>
            Theme: <span className="mobile-more-item__value">{currentTheme()}</span>
          </button>
        </div>
      )}

      {navItems.map((item) => {
        const isActive = activeId === item.id;
        const Icon = isActive ? item.Filled : item.Outline;
        return (
          <button
            key={item.id}
            type="button"
            className={`mobile-nav-item ${isActive ? 'active' : ''}`}
            onClick={() => handleTap(item)}
            aria-label={item.label}
            aria-current={isActive ? 'page' : undefined}
          >
            <span className="mobile-nav-icon"><Icon /></span>
            {isActive && <span className="mobile-nav-label">{item.label}</span>}
          </button>
        );
      })}

      <button
        type="button"
        className={`mobile-nav-item ${activeId === 'more' || moreOpen ? 'active' : ''}`}
        onClick={() => setMoreOpen(!moreOpen)}
        aria-label="More options"
        aria-expanded={moreOpen}
        aria-haspopup="menu"
      >
        <span className="mobile-nav-icon">
          {(activeId === 'more' || moreOpen) ? <MoreFilled /> : <MoreOutline />}
        </span>
        {(activeId === 'more' || moreOpen) && <span className="mobile-nav-label">More</span>}
      </button>
    </nav>
  );
};
